import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import posthog from 'posthog-js'
import {
  Actions,
  Intro,
  IntroText,
  PensionForm,
  SecondaryAction,
  Wrapper,
  DayGrid,
} from './PensionConfig.styles'
import { useInput } from '../../components'
import { default as Button } from '../../components/Buttons/BaseButton'
import PensionConfigForm, {
  validatePaymentDay,
  validatePensionValue,
} from './PensionConfigForm'
import {
  getPensionConfig,
  setPensionConfig,
} from '../OnboardingPension/OnboardingPension.slice.js'
import Spinner from '../../components/Spinners/Spinner.jsx'
import MessageNotice from '../../components/MessageNotice/MessageNotice'
import {
  EXTERNAL_LINKS,
  METHOD,
  getImmPercentageFromConfig,
  getPaymentDayFromConfig,
  getPensionConfigInputsKey,
  getUtmQuantityFromConfig,
  normalizePensionMethod,
} from '../../config/settings/constants.js'
import {
  SUPPORT_TOPIC,
  buildSupportHelpPath,
} from '../../config/settings/support'
import { fetchLatestImm } from '../../services/onboarding'
import { triggerPensionCalculationForCurrentUser } from '../../services/notifier'
import { DayButton } from '../OnboardingPension/OnboardingPension.styles'
import { preferencesSaveMessage } from './preferencesSaveMessage'
import { getLogger } from '../../core/debuggers'
import {
  Field,
  FieldError,
  FormHint,
  FormLabel,
} from './PensionConfigForm.styles'

const logger = getLogger('PensionConfig.container')

const PensionConfig = () => {
  const { config, fetchStatus, updateStatus, error: configError } = useSelector(
    (state) => state.adminPensionConfig
  )
  const dispatch = useDispatch()

  const configuredPaymentDay = useMemo(
    () => getPaymentDayFromConfig(config),
    [config]
  )

  const [method, setMethod] = useState(METHOD.UTM)
  const [methodError, setMethodError] = useState('')
  const [valueError, setValueError] = useState('')
  const [paymentDay, setPaymentDay] = useState(configuredPaymentDay)
  const [paymentError, setPaymentError] = useState('')
  const [submitError, setSubmitError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [saveNoticeType, setSaveNoticeType] = useState('success')
  const [reconciling, setReconciling] = useState(false)
  const [hadLegacyFixedConfig, setHadLegacyFixedConfig] = useState(false)
  const [upgradeToUtm, setUpgradeToUtm] = useState(false)
  const [immValue, setImmValue] = useState(null)

  const {
    value: amountValue,
    setValue: amountSetValue,
    onChange: handleAmountChange,
  } = useInput({
    initialValue: getUtmQuantityFromConfig(config),
  })

  const {
    value: percentageValue,
    setValue: percentageSetValue,
    onChange: handlePercentageChange,
  } = useInput({
    initialValue: getImmPercentageFromConfig(config),
  })

  const isSaving = updateStatus === 'loading' || reconciling
  const storedMethod = config ? normalizePensionMethod(config.method) : null
  const isUtmLocked = storedMethod === METHOD.UTM
  const isLegacyImm = storedMethod === METHOD.IMM
  const isLegacyFixed = storedMethod === METHOD.FIXED_AMOUNT
  const isLegacyMethod = isLegacyImm || isLegacyFixed
  const showMethodPicker = !storedMethod
  const effectiveMethod = isUtmLocked || upgradeToUtm || isLegacyFixed
    ? METHOD.UTM
    : method

  const handleMethodChange = (nextMethod) => {
    if (isUtmLocked) return
    setMethod(nextMethod)
    setMethodError('')
    setValueError('')
    setSubmitError(null)
    setSaveSuccess(false)
    amountSetValue('')
    percentageSetValue('')
  }

  const handleStartUtmUpgrade = () => {
    setUpgradeToUtm(true)
    setMethod(METHOD.UTM)
    setMethodError('')
    setValueError('')
    setSubmitError(null)
    setSaveSuccess(false)
    amountSetValue('')
    percentageSetValue('')
  }

  const handleCancelUtmUpgrade = () => {
    setUpgradeToUtm(false)
    setMethod(METHOD.IMM)
    setMethodError('')
    setValueError('')
    setSubmitError(null)
    setSaveSuccess(false)
    amountSetValue('')
    percentageSetValue(getImmPercentageFromConfig(config))
  }

  const handleAmountInputChange = (event) => {
    setSaveSuccess(false)
    handleAmountChange(event)
  }

  const handlePercentageInputChange = (event) => {
    setSaveSuccess(false)
    handlePercentageChange(event)
  }

  const handleSave = async (event) => {
    event.preventDefault()
    setSubmitError(null)
    setSaveSuccess(false)
    setSaveMessage('')

    const methodToSave = isUtmLocked ? METHOD.UTM : effectiveMethod

    const validation = validatePensionValue(
      methodToSave,
      percentageValue,
      amountValue
    )
    if (!validation.valid) {
      setValueError(validation.error)
      return
    }
    setValueError('')

    const dayToSave = paymentDay ?? getPaymentDayFromConfig(config)
    const dayValidation = validatePaymentDay(dayToSave)
    if (!dayValidation.valid) {
      setPaymentError(dayValidation.error)
      return
    }
    setPaymentError('')

    const factorChanged =
      getPensionConfigInputsKey(config) !==
      getPensionConfigInputsKey({
        method: methodToSave,
        amount: methodToSave === METHOD.UTM ? amountValue : '',
        percentage: methodToSave === METHOD.IMM ? percentageValue : '',
      })
    const paydayChanged =
      Number(dayToSave) !== Number(getPaymentDayFromConfig(config))

    try {
      await dispatch(
        setPensionConfig({
          method: methodToSave,
          amount: methodToSave === METHOD.UTM ? amountValue : '',
          percentage:
            methodToSave === METHOD.IMM ? percentageValue : '',
          paymentDay: dayToSave,
        })
      ).unwrap()
      posthog.capture('pension_config_saved', {
        method: methodToSave,
        payment_day: dayToSave,
        upgraded_from_legacy: Boolean(isLegacyMethod && upgradeToUtm),
        factor_changed: factorChanged,
        payday_changed: paydayChanged,
      })

      let senderStatus = null
      if (factorChanged) {
        setReconciling(true)
        try {
          const result = await triggerPensionCalculationForCurrentUser()
          senderStatus = result?.status || 'ok'
        } catch (senderError) {
          logger.error(senderError)
          senderStatus = 'sender_failed'
        } finally {
          setReconciling(false)
        }
      }

      setSaveMessage(
        preferencesSaveMessage({
          factorChanged,
          paydayChanged,
          senderStatus,
        })
      )
      setSaveNoticeType(senderStatus === 'sender_failed' ? 'warning' : 'success')
      setSaveSuccess(true)
      setHadLegacyFixedConfig(false)
      setUpgradeToUtm(false)
    } catch (error) {
      setSubmitError(
        typeof error === 'string'
          ? error
          : error?.message ||
              'No pudimos guardar tu configuración. Inténtalo de nuevo.'
      )
    }
  }

  useEffect(() => {
    if (!config) return

    const nextStoredMethod = normalizePensionMethod(config.method)
    const nextIsLegacyFixed = nextStoredMethod === METHOD.FIXED_AMOUNT
    setHadLegacyFixedConfig(nextIsLegacyFixed)
    setUpgradeToUtm(false)
    setMethod(nextIsLegacyFixed ? METHOD.UTM : nextStoredMethod)
    amountSetValue(
      nextStoredMethod === METHOD.UTM ? getUtmQuantityFromConfig(config) : ''
    )
    percentageSetValue(
      nextStoredMethod === METHOD.IMM
        ? getImmPercentageFromConfig(config)
        : ''
    )
    setPaymentDay(configuredPaymentDay)
  }, [config, configuredPaymentDay, amountSetValue, percentageSetValue])

  useEffect(() => {
    dispatch(getPensionConfig())
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    fetchLatestImm()
      .then((imm) => {
        if (!cancelled) setImmValue(imm?.value ?? null)
      })
      .catch(() => {
        if (!cancelled) setImmValue(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (fetchStatus === 'loading') return <Spinner />
  if (configError !== null) return <p>Error: {configError}</p>

  const introText = isLegacyImm && !upgradeToUtm
    ? 'Tu pensión usa % del sueldo mínimo (causas antiguas).'
    : 'Con este dato estimamos tu monto cada mes.'

  // Prefer the in-form selection; fall back to Redux/DB value so the
  // configured day stays marked as soon as config hydrates.
  const selectedPaymentDay =
    paymentDay != null ? paymentDay : configuredPaymentDay

  return (
    <Wrapper>
      <Intro>
        <IntroText>{introText}</IntroText>
      </Intro>

      {hadLegacyFixedConfig && (
        <MessageNotice type="warning">
          Detectamos una configuración anterior en monto fijo (pesos). Ese modo
          ya no se edita aquí. Solicita la conversión a UTM en{' '}
          <a
            href={EXTERNAL_LINKS.TRAMITE_FACIL_UTM}
            target="_blank"
            rel="noopener noreferrer"
          >
            Trámite Fácil
          </a>{' '}
          o{' '}
          <Link to={buildSupportHelpPath(SUPPORT_TOPIC.LEGACY_FIXED_AMOUNT)}>
            escríbenos y te orientamos
          </Link>
          .
        </MessageNotice>
      )}

      {isLegacyImm && !upgradeToUtm && (
        <MessageNotice type="info">
          Para pasar a UTM, solicítalo en{' '}
          <a
            href={EXTERNAL_LINKS.TRAMITE_FACIL_UTM}
            target="_blank"
            rel="noopener noreferrer"
          >
            Trámite Fácil del Poder Judicial
          </a>
          . Cuando tengas la resolución, actualiza el método aquí.
        </MessageNotice>
      )}

      {upgradeToUtm && isLegacyImm && (
        <MessageNotice type="info">
          Ingresa las UTM de tu nueva resolución. Si aún no la tienes,{' '}
          <SecondaryAction type="button" onClick={handleCancelUtmUpgrade}>
            vuelve al % del sueldo mínimo
          </SecondaryAction>
          .
        </MessageNotice>
      )}

      <PensionForm onSubmit={handleSave}>
        <PensionConfigForm
          method={effectiveMethod}
          amount={amountValue}
          percentage={percentageValue}
          onMethodChange={handleMethodChange}
          onAmountChange={handleAmountInputChange}
          onPercentageChange={handlePercentageInputChange}
          methodError={methodError}
          valueError={valueError || submitError}
          showMethod={showMethodPicker}
          showMethodLabel={showMethodPicker}
          immValue={immValue}
          disabled={isSaving}
        />

        {isLegacyImm && !upgradeToUtm && (
          <SecondaryAction
            type="button"
            onClick={handleStartUtmUpgrade}
            disabled={isSaving}
          >
            Ya tengo la resolución en UTM
          </SecondaryAction>
        )}

        <Field>
          <FormLabel>Día de pago según tu resolución</FormLabel>
          <FormHint>
            Usaremos este día para tus recordatorios. Si lo cambias, los avisos
            que aún no salen usan el día nuevo.
          </FormHint>
          <DayGrid role="listbox" aria-label="Día de pago según tu resolución">
            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
              <DayButton
                key={day}
                type="button"
                role="option"
                aria-selected={selectedPaymentDay === day}
                $active={selectedPaymentDay === day}
                disabled={isSaving}
                onClick={() => {
                  setPaymentDay(day)
                  setPaymentError('')
                  setSaveSuccess(false)
                }}
              >
                {day}
              </DayButton>
            ))}
          </DayGrid>
          {paymentError && <FieldError>{paymentError}</FieldError>}
        </Field>

        {saveSuccess && saveMessage && (
          <MessageNotice type={saveNoticeType}>
            {saveMessage}
          </MessageNotice>
        )}

        <Actions>
          <Button type="submit" loading={isSaving} width="100%">
            Guardar
          </Button>
        </Actions>
      </PensionForm>
    </Wrapper>
  )
}

export default PensionConfig
